import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Predefined zoom presets for common IEM bands
const ZOOM_PRESETS = [
  { id: "full", label: "Completo", min: 480, max: 980 },
  { id: "uhf_low", label: "UHF Bajo (470-600)", min: 470, max: 600 },
  { id: "uhf_mid", label: "UHF Medio (600-700)", min: 600, max: 700 },
  { id: "uhf_high", label: "UHF Alto (700-900)", min: 700, max: 900 },
  { id: "lte_zone", label: "Zona LTE (680-820)", min: 680, max: 820 },
  // Brand-specific presets
  { id: "anleon_s2", label: "Anleon S2 (561-568)", min: 558, max: 572 },
  { id: "anleon_s3", label: "Anleon S3 (520-578)", min: 515, max: 585 },
  { id: "sennheiser_ew", label: "Sennheiser EW (470-865)", min: 470, max: 865 },
  { id: "shure_psm300", label: "Shure PSM300 (470-506)", min: 465, max: 515 },
  { id: "shure_psm900", label: "Shure PSM900 (470-560)", min: 465, max: 565 },
  { id: "audio_technica", label: "Audio-Technica (470-608)", min: 465, max: 615 },
];

/**
 * SpectrumZoom Component
 * Provides zoom controls for RF spectrum visualization
 */
export function SpectrumZoom({ 
  minFreq = 480, 
  maxFreq = 980, 
  onRangeChange, 
  currentMin = 480, 
  currentMax = 980,
  compact = false 
}) {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState("full");
  const [customMin, setCustomMin] = useState(currentMin);
  const [customMax, setCustomMax] = useState(currentMax);

  const handlePresetChange = (presetId) => {
    setSelectedPreset(presetId);
    const preset = ZOOM_PRESETS.find((p) => p.id === presetId);
    if (preset && onRangeChange) {
      setCustomMin(preset.min);
      setCustomMax(preset.max);
      onRangeChange(preset.min, preset.max);
    }
  };

  const handleSliderChange = (values) => {
    const [min, max] = values;
    if (max - min >= 10) { // Minimum 10 MHz range
      setCustomMin(min);
      setCustomMax(max);
      setSelectedPreset("custom");
      if (onRangeChange) {
        onRangeChange(min, max);
      }
    }
  };

  const handleZoomIn = () => {
    const center = (customMin + customMax) / 2;
    const range = (customMax - customMin) / 2;
    const newRange = Math.max(range * 0.7, 5); // Min 10 MHz total
    const newMin = Math.max(minFreq, center - newRange);
    const newMax = Math.min(maxFreq, center + newRange);
    setCustomMin(newMin);
    setCustomMax(newMax);
    setSelectedPreset("custom");
    if (onRangeChange) {
      onRangeChange(newMin, newMax);
    }
  };

  const handleZoomOut = () => {
    const center = (customMin + customMax) / 2;
    const range = (customMax - customMin) / 2;
    const newRange = Math.min(range * 1.5, (maxFreq - minFreq) / 2);
    const newMin = Math.max(minFreq, center - newRange);
    const newMax = Math.min(maxFreq, center + newRange);
    setCustomMin(newMin);
    setCustomMax(newMax);
    setSelectedPreset("custom");
    if (onRangeChange) {
      onRangeChange(newMin, newMax);
    }
  };

  const handleReset = () => {
    setCustomMin(minFreq);
    setCustomMax(maxFreq);
    setSelectedPreset("full");
    if (onRangeChange) {
      onRangeChange(minFreq, maxFreq);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Select value={selectedPreset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-40 h-8 text-xs bg-[#0A0A0A] border-white/10">
            <SelectValue placeholder="Zoom" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            {ZOOM_PRESETS.map((preset) => (
              <SelectItem key={preset.id} value={preset.id} className="text-xs">
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleZoomIn}
            title="Acercar"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleZoomOut}
            title="Alejar"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleReset}
            title="Restablecer"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
        <span className="text-xs font-mono text-[#71717A]">
          {customMin.toFixed(0)}-{customMax.toFixed(0)} MHz
        </span>
      </div>
    );
  }

  return (
    <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Maximize2 className="w-4 h-4 text-[#34C759]" />
          <h3 className="font-medium text-[#F4F4F5] text-sm">Zoom del Espectro</h3>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[#71717A] hover:text-[#F4F4F5]"
            onClick={handleZoomIn}
            title="Acercar"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[#71717A] hover:text-[#F4F4F5]"
            onClick={handleZoomOut}
            title="Alejar"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-[#71717A] hover:text-[#F4F4F5]"
            onClick={handleReset}
            title="Restablecer vista completa"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Preset Selector */}
      <div className="mb-4">
        <label className="text-xs text-[#71717A] mb-2 block">Presets de Banda</label>
        <Select value={selectedPreset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10">
            <SelectValue placeholder="Seleccionar preset" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[300px]">
            <SelectItem value="full">Vista Completa (480-980 MHz)</SelectItem>
            <SelectItem value="custom" disabled>
              Personalizado
            </SelectItem>
            <div className="px-2 py-1 text-[10px] text-[#71717A] bg-[#0A0A0A]">
              RANGOS GENERALES
            </div>
            {ZOOM_PRESETS.slice(1, 5).map((preset) => (
              <SelectItem key={preset.id} value={preset.id}>
                {preset.label}
              </SelectItem>
            ))}
            <div className="px-2 py-1 text-[10px] text-[#71717A] bg-[#0A0A0A]">
              POR MARCA
            </div>
            {ZOOM_PRESETS.slice(5).map((preset) => (
              <SelectItem key={preset.id} value={preset.id}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Range Slider */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-[#71717A]">Rango de Frecuencia</label>
          <span className="text-xs font-mono text-[#34C759]">
            {customMin.toFixed(0)} - {customMax.toFixed(0)} MHz
          </span>
        </div>
        <Slider
          value={[customMin, customMax]}
          onValueChange={handleSliderChange}
          min={minFreq}
          max={maxFreq}
          step={1}
          className="py-4"
        />
        <div className="flex justify-between text-[10px] text-[#71717A]">
          <span>{minFreq} MHz</span>
          <span>{maxFreq} MHz</span>
        </div>
      </div>

      {/* Quick Info */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 bg-[#0A0A0A] rounded-sm">
          <p className="text-[10px] text-[#71717A]">Ancho</p>
          <p className="font-mono text-sm text-[#F4F4F5]">
            {(customMax - customMin).toFixed(0)} MHz
          </p>
        </div>
        <div className="p-2 bg-[#0A0A0A] rounded-sm">
          <p className="text-[10px] text-[#71717A]">Centro</p>
          <p className="font-mono text-sm text-[#F4F4F5]">
            {((customMin + customMax) / 2).toFixed(0)} MHz
          </p>
        </div>
        <div className="p-2 bg-[#0A0A0A] rounded-sm">
          <p className="text-[10px] text-[#71717A]">Zoom</p>
          <p className="font-mono text-sm text-[#F4F4F5]">
            {(((maxFreq - minFreq) / (customMax - customMin)) * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      {/* LTE Warning */}
      {customMin < 806 && customMax > 698 && (
        <div className="mt-3 p-2 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-sm">
          <p className="text-[10px] text-[#FF3B30] flex items-center gap-1">
            <span className="w-2 h-2 bg-[#FF3B30] rounded-full" />
            Zona LTE visible (698-806 MHz) - Evitar asignar frecuencias aquí
          </p>
        </div>
      )}
    </div>
  );
}

export default SpectrumZoom;
